import { inject, Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import type { ScLinkField } from '@core/jss-models';
import { TrackToolService } from '@analytics/events';
import { openGenericModal } from '@innogy/common-ui-modals';
import {
  GenericModalPosition,
  type GenericModalProps,
} from '@legacy/common-ui-shared-interfaces';
import type { SingleAnswerQuestionOption } from '@feh/shared-ui';

import {
  answerMappingError,
  leaveQuestionnaire,
  loadNextQuestion,
  openInfoModal,
  submitQuestionnaireAnswer,
} from './homebattery-questionnaire.actions';
import { selectHomebatteryQuestionnaireStructure } from './homebattery-questionnaire.selectors';
import { parsedHomebatteryFunnelToolTrackingConfig } from './homebattery-questionnaire.analytics-config';
import { selectHomeBatteryFunnelSettings } from '../funnel/homebattery-funnel.selectors';
import { onHomeBatteryFunnelNavigationAction } from '../funnel/homebattery-funnel.actions';

@Injectable()
export class HomebatteryQuestionnaireEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly injector = inject(Injector);

  handleAnswerSubmit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(submitQuestionnaireAnswer),
      concatLatestFrom((_) => [
        this.store.select(selectHomebatteryQuestionnaireStructure),
        this.store.select(selectHomeBatteryFunnelSettings),
      ]),
      mergeMap(([action, structure, funnelSettings]) => {
        const target =
          action.question.type === 'single-answer-question'
            ? (action.answer as SingleAnswerQuestionOption).target
            : action.question.target;

        switch (target.type) {
          case 'next-question': {
            const next = target.questionName;
            return next in structure
              ? [
                  loadNextQuestion({
                    question: structure[next],
                  }),
                ]
              : [answerMappingError()];
          }
          case 'link': {
            const funnelKey = target.funnelKey as keyof typeof funnelSettings;
            const next = funnelSettings[funnelKey] as ScLinkField;
            return [
              onHomeBatteryFunnelNavigationAction({ page: next }),
              leaveQuestionnaire(),
            ];
          }
          default:
            return [answerMappingError()];
        }
      })
    );
  });

  handleLeaveQuestionnaire$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(leaveQuestionnaire),
        tap(() => {
          /**
           * WTA-17: resolve injector context
           */
          const trackToolService = this.injector.get(TrackToolService);
          trackToolService.completeTool(
            parsedHomebatteryFunnelToolTrackingConfig
          );
        })
      ),
    { dispatch: false }
  );

  openInfoModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openInfoModal),
      concatLatestFrom(() =>
        this.store.select(selectHomeBatteryFunnelSettings)
      ),
      map(([info, funnelSettings]) => {
        const modalData = funnelSettings[
          info.funnelKey as keyof typeof funnelSettings
        ] as unknown as GenericModalProps;
        return openGenericModal({
          ...modalData,
          position: GenericModalPosition.LEFT,
        });
      })
    )
  );
}
